import { useEffect, useState } from 'react';
import { currentLayout } from '../utils/layoutMode';

// Fullscreen for the video overlays (VideoWatchOverlay, ScreenShareOverlay).
//
// Fullscreens the whole overlay (title/surface/camera strip/controls), not
// just the raw <video>/<iframe> - so the custom control bar stays usable
// instead of getting hidden along with the rest of the page.
//
// iPhone Safari has no element fullscreen at all (only <video>'s own native
// player, which would drop our controls and the camera strip) - there the
// overlay goes "pseudo" fullscreen instead: a CSS class that pins it over
// the whole viewport (.VideoOverlayPseudoFullscreen, Groups.css). Anywhere
// requestFullscreen exists - every desktop/Electron build - the path is
// exactly the native one it always was.
export function useOverlayFullscreen(ref) {
  const [native, setNative] = useState(false);
  const [pseudo, setPseudo] = useState(false);

  // document.fullscreenElement is the source of truth (not local state
  // alone) since the browser can also exit fullscreen on its own - Esc key,
  // or the overlay unmounting because someone else stopped the session -
  // without ever going through toggleFullscreen below.
  useEffect(() => {
    const el = ref.current;
    const handleChange = () => {
      const on = document.fullscreenElement === el;
      setNative(on);
      // Phones: a video goes fullscreen sideways. Android allows locking the
      // orientation only while fullscreen (iOS never gets here - no element
      // fullscreen); desktop browsers reject it, and aren't asked anyway.
      const orientation = window.screen && window.screen.orientation;
      if (!orientation || !currentLayout()) return;
      if (on && orientation.lock) orientation.lock('landscape').catch(() => {});
      else if (!on && orientation.unlock) orientation.unlock();
    };
    document.addEventListener('fullscreenchange', handleChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleChange);
      if (document.fullscreenElement === el) document.exitFullscreen().catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Same Escape-to-leave a keyboard user gets from native fullscreen.
  useEffect(() => {
    if (!pseudo) return undefined;
    const onKeyDown = (e) => { if (e.key === 'Escape') setPseudo(false); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [pseudo]);

  const toggleFullscreen = () => {
    if (pseudo) {
      setPseudo(false);
    } else if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    } else if (ref.current && ref.current.requestFullscreen) {
      ref.current.requestFullscreen().catch(() => {});
    } else if (ref.current) {
      setPseudo(true);
    }
  };

  const leaveFullscreen = () => {
    setPseudo(false);
    if (document.fullscreenElement === ref.current) document.exitFullscreen().catch(() => {});
  };

  return { isFullscreen: native || pseudo, pseudoFullscreen: pseudo, toggleFullscreen, leaveFullscreen };
}
